import { useContext, useEffect, useRef, useState } from "react";
import ChatBox from "../../../../components/sections/front/communication/ChatBox";
import SocketContext, { useSocket } from "../../../../context/SocketContext";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  chatDetails,
  chatLists,
  chatMessageSend,
  messageList,
  messageSeen,
} from "../../../../services/chatService";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { lngDetect } from "../../../../helpers/lngDetect";
import { loadDataOnScroll, replaceOldData } from "../../../../helpers";
import { useDebounce } from "../../../../hooks/useDebounce";
import { useSelector } from "react-redux";

const CommunicationEvent = () => {
  const { event } = useOutletContext();
  const navigate = useNavigate();
  const myMessagesDivRef = useRef(null);
  const queryClient = useQueryClient();
  const { socket } = useContext(SocketContext);
  const { session } = useSelector((state) => state?.authProvider);

  const location = useLocation();
  const queryObject = Object.fromEntries(new URLSearchParams(location.search));
  const [messageFilter, setMessageFilter] = useState({
    page: 1,
    limit: 10,
    sortQuery: "-createdAt",
  });
  const [filter, setFilter] = useState({
    page: 1,
    limit: 10,
    sortQuery: "-latestMessage.sentAt",
  });
  const [chatSearch, setChatSearch] = useState("");
  const chat_search = useDebounce(chatSearch, 400);
  const [lastMsgScrollPosition, setLastMsgScrollPosition] = useState(0);

  useEffect(() => {
    const el = myMessagesDivRef.current;
    if (!el) return;

    const handleWheel = (e) => {
      e.preventDefault();
      // Reverse the scroll direction
      el.scrollTop -= e.deltaY;
    };

    el.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      el.removeEventListener("wheel", handleWheel);
    };
  }, []);

  const { data: chatListData } = useQuery({
    queryKey: ["chat-list", filter, event?.data?._id, chat_search],
    placeholderData: keepPreviousData,
    enabled: !!event?.data?._id,
    queryFn: async () => {
      const res = await chatLists({
        ...filter,
        referenceId: event?.data?._id,
        ...(chat_search && { keyword: chat_search }),
      });

      const update_data = res?.docs?.data?.map((item) => {
        return {
          ...item,
          name: lngDetect(item?.referenceInfo?.title),
          avatar: item?.referenceInfo?.gallery?.url,
          communicationGroupId: chatDetailsData?.data?._id,
        };
      });

      const marge_arr = [...(chatListData?.data || []), ...update_data];
      return {
        ...res?.docs,
        data: replaceOldData({
          oldData: marge_arr,
          newData: update_data,
        }),
      };
    },
  });

  const { data: chatDetailsData } = useQuery({
    queryKey: ["chat-details", queryObject?.id],
    enabled: !!queryObject?.id && chatListData?.data?.length > 0,
    queryFn: async () => {
      const res = await chatDetails({ id: queryObject?.id, type: "front" });
      return res;
    },
  });

  const {
    data: messages,
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["message-list", chatDetailsData?.data?._id, messageFilter],
    enabled: !!chatDetailsData?.data?._id,
    queryFn: async () => {
      if (messages?.chatDetailsData !== chatDetailsData?.data?._id) {
        queryClient.setQueriesData(
          {
            queryKey: ["message-list"],
            exact: false,
          },
          (state) => {
            return {};
          }
        );
      }
      const res = await messageList({
        communicationGroupId: chatDetailsData?.data?._id,
        ...messageFilter,
      });

      if (res?.docs?.data?.length > 0 && messageFilter?.page > 1) {
        myMessagesDivRef.current.scrollTop = lastMsgScrollPosition;
      }
      const data = res?.docs?.data.sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
      );

      const update_data = [...(data || []), ...(messages?.data || [])]?.filter(
        (item, index, self) =>
          index === self.findIndex((t) => t?._id === item?._id)
      );

      if (res?.docs?.status === 200) {
        messageSeen({
          communicationGroupId: chatDetailsData?.data?._id,
        }).then((resp) => {
          const active_chat = (chatListData?.data || [])?.find(
            (chat) => chat?._id === chatDetailsData?.data?._id
          );
          const find_latest_message = update_data?.find(
            (msg) => msg?._id === active_chat?.latestMessage?.messageId
          );

          if (resp?.status === 200) {
            socket?.emit("messageUnreadCountEvent", {
              user: session?.user?._id,
            });
            socket?.emit("messageUnreadCountEvent", {
              user: session?.user?._id,
              countFor: "event",
              referenceId: event?.data?._id,
            });
            socket?.emit("messageSeenEvent", {
              send_by_user: active_chat?.latestMessage?.sentBy,
              communicationGroupId: chatDetailsData?.data?._id,
              message: find_latest_message,
              seen_by_user: {
                _id: session?.user?._id,
                image: { url: session?.user?.image },
                firstName: session?.user?.firstName,
                lastName: session?.user?.lastName,
                seenAt: new Date(),
              },
            });
          }
        });
      }

      if (messages?.chatDetailsData === chatDetailsData?.data?._id) {
        return {
          ...res?.docs,
          data: update_data,
          chatDetailsData: chatDetailsData?.data?._id,
        };
      } else {
        return {
          ...res?.docs,
          data: [...(data || [])],
          chatDetailsData: chatDetailsData?.data?._id,
        };
      }
    },
    placeholderData: keepPreviousData,
  });

  const { mutateAsync: onSend, error } = useMutation({
    mutationFn: async ({ value, uploadPercentage }) => {
      if (
        (!value?.attachments && !value?.text) ||
        (Array.isArray(value?.attachments) &&
          value?.attachments?.length === 0 &&
          !value?.text)
      ) {
        return;
      }
      const payload = {
        ...value,
        content: value?.text,
        communicationGroupId: chatDetailsData?.data?._id,
      };
      delete payload?.text;

      const res = await chatMessageSend(payload, uploadPercentage);
      return res;
    },
    onSuccess: (res) => {
      if (res?.status === 200 || res?.status === 201) {
        setLastMsgScrollPosition(0);
        myMessagesDivRef.current.scrollTop = 0;
        socket?.emit("getMessageDetailEvent", {
          res: res || {},
          send_by_user: session?.user?._id,
        });
      }
    },
  });

  useSocket(
    "getMessageDetailEventResponse",
    async (payload) => {
      if (payload) {
        const { res, send_by_user } = payload;
        if (res?.data?.communicationGroupId === chatDetailsData?.data?._id) {
          // "update message list for both who send and who receive"
          queryClient?.setQueryData(
            ["message-list", chatDetailsData?.data?._id, messageFilter],
            (state) => {
              const state_data = JSON.parse(JSON.stringify(state || {}));
              state_data.data = [...state_data?.data, res?.data];
              return { ...state_data };
            }
          );
          // "update message list for both who send and who receive"------> end
          if (session?.user?._id !== send_by_user) {
            // "seen by receive user who are in the same inbox"
            const resp = await messageSeen({
              communicationGroupId: chatDetailsData?.data?._id,
            });
            if (resp?.status === 200) {
              socket?.emit("messageSeenEvent", {
                communicationGroupId: res?.data?.communicationGroupId,
                send_by_user: session?.user?._id,
                seen_by_user: {
                  _id: session?.user?._id,
                  image: { url: session?.user?.image },
                  firstName: session?.user?.firstName,
                  lastName: session?.user?.lastName,
                  seenAt: new Date(),
                },
                message: res?.data,
              });
            }
          }
        }
        queryClient?.setQueryData(["chat-list", chat_search], (state) => {
          const state_data = JSON.parse(JSON.stringify(state || {}));
          state_data.data = state_data.data?.map((item) =>
            item?.referenceInfo?._id === res?.data?.referenceId
              ? {
                  ...item,
                  latestMessage: {
                    message: res?.data?.content,
                    sentAt: res?.data?.createdAt,
                    sentBy: res?.data?.user,
                    messageId: res?.data?._id,
                  },
                  ...(send_by_user !== session?.user?._id && {
                    unreadCount: (item?.unreadCount || 0) + 1,
                  }),
                }
              : item
          );
          return { ...state_data };
        });
        queryClient?.invalidateQueries({
          queryKey: ["chat-list"],
          exact: false,
        });
      }
    },
    [
      chatDetailsData?.data?._id,
      chat_search,
      messageFilter,
      messages,
      session?.user?._id,
    ]
  );

  useSocket(
    "messageSeenEventResponse",
    async ({ send_by_user, communicationGroupId, seen_by_user, message }) => {
      if (communicationGroupId === chatDetailsData?.data?._id) {
        // update message list for all user ===============>
        if (message?._id) {
          queryClient?.setQueryData(
            ["message-list", chatDetailsData?.data?._id, messageFilter],
            (state) => {
              const state_data = JSON.parse(JSON.stringify(state || {}));
              state_data.data = (state_data?.data || [])?.map((msg) =>
                msg?._id === message?._id
                  ? {
                      ...msg,
                      seenBy: [
                        ...(msg?.seenBy || []),
                        {
                          user: seen_by_user?._id,
                          seenAt: seen_by_user?.seenAt,
                        },
                      ]?.filter(
                        (item, index, self) =>
                          index === self.findIndex((t) => t?._id === item?._id)
                      ),
                      seenUserData: [
                        ...(msg?.seenUserData || []),
                        seen_by_user,
                      ]?.filter(
                        (item, index, self) =>
                          index === self.findIndex((t) => t?._id === item?._id)
                      ),
                    }
                  : msg
              );
              return { ...state_data };
            }
          );
          queryClient?.invalidateQueries({
            queryKey: ["chat-list"],
            exact: false,
          });
        }
        // update message list for all user (end) ===============>
      }
    },
    [chatDetailsData?.data?._id, messageFilter, messages, session?.user?._id]
  );

  return (
    <ChatBox
      selectChatHandler={(val) => {
        if (queryObject?.id !== val?.uid) {
          navigate(`${location?.pathname}?id=${val?.uid}`);
          setLastMsgScrollPosition(0);
          myMessagesDivRef.current.scrollTop = 0;
        }
      }}
      searchValue={chatSearch}
      onSearch={setChatSearch}
      messageLoading={isLoading || isFetching}
      chatList={chatListData?.data}
      messages={messages?.data?.map((msg, i, arr) => {
        if (msg?.userInfo?._id !== session?.user?._id) return msg;

        let seen_by_user = (msg?.seenUserData || []).filter(
          (item) => item?._id !== session?.user?._id
        );

        const nextMsgSameUser = arr
          .slice(i + 1)
          .find((m) => m?.userInfo?._id === session?.user?._id);

        if (nextMsgSameUser) {
          const nextSeenIds = (nextMsgSameUser.seenUserData || []).map(
            (u) => u?._id
          );
          seen_by_user = seen_by_user.filter(
            (s) => !nextSeenIds.includes(s?._id)
          );
        }

        return { ...msg, seen_by_user };
      })}
      chatInfo={chatDetailsData?.data}
      onSend={onSend}
      myMessagesDivRef={myMessagesDivRef}
      onMessageScroll={(e) => {
        loadDataOnScroll({
          event: e,
          metadata: messages?.metadata,
          setData: setMessageFilter,
          setLastScrollPosition: setLastMsgScrollPosition,
        });
      }}
    />
  );
};

export default CommunicationEvent;
